import { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck } from 'lucide-react';

/**
 * How it works — three columns, shown together, not tabbed.
 *
 * Previous pass was a pill-selector crossfading a single illustration panel;
 * rejected, and it also had a real bug — the three illustrations were
 * different heights, so the panel visibly resized on every crossfade. This
 * version sidesteps that by construction: all three steps render
 * simultaneously in fixed-size cards (`ILLUSTRATION_HEIGHT` below, shared by
 * all three), not swapped in and out of one shared frame, so there's no size
 * mismatch to have. Each illustration plays its own small reveal animation
 * once, staggered left to right, when the section scrolls into view.
 */

const ILLUSTRATION_HEIGHT = 'h-64'; // shared by all three — the actual fix for the size-mismatch bug

const STEPS = [
  {
    title: 'Set the terms',
    body: 'Scope, milestones, and payment terms, including a late-payment interest rate, agreed before work starts.',
  },
  {
    title: 'Send the link, they sign',
    body: 'Your client signs with an OTP sent to their email. No account for them to create, no app to install.',
  },
  {
    title: 'Deliver with proof attached',
    body: 'Every submission, approval and payment is timestamped and backed by evidence automatically, as you work.',
  },
] as const;

/* ── Illustration 1 — the contract form ──────────────────────────────── */
function ContractFormIllustration() {
  return (
    <div className="w-full max-w-[240px]">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">New contract</span>
        <span className="w-1.5 h-1.5 rounded-full bg-[#3cb44f]" />
      </div>
      <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 mb-2">
        <p className="text-[9px] text-white/35 mb-0.5">Project</p>
        <motion.p
          className="text-[13px] text-white font-medium overflow-hidden whitespace-nowrap"
          initial={{ width: 0 }}
          whileInView={{ width: 'auto' }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
        >
          Website redesign
        </motion.p>
      </div>
      <div className="flex gap-2 mb-3">
        <div className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
          <p className="text-[9px] text-white/35 mb-0.5">Milestones</p>
          <p className="text-[13px] text-white font-medium">3</p>
        </div>
        <motion.div
          className="flex-1 rounded-lg border border-[#3cb44f]/30 bg-[#3cb44f]/10 px-3 py-2"
          initial={{ opacity: 0.25 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <p className="text-[9px] text-[#3cb44f]/70 mb-0.5">Late fee</p>
          <p className="text-[13px] text-[#3cb44f] font-medium">12% p.a.</p>
        </motion.div>
      </div>
      <motion.div
        className="rounded-lg bg-[#3cb44f] text-black text-center text-[12px] font-bold py-2"
        initial={{ opacity: 0.4 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 1.4 }}
      >
        Send to client
      </motion.div>
    </div>
  );
}

/* ── Illustration 2 — OTP entry ──────────────────────────────────────── */
function OtpSignIllustration() {
  const digits = ['4', '1', '9', '2'];
  return (
    <div className="w-full max-w-[240px]">
      <p className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Verify to sign</p>
      <p className="text-[11px] text-white/45 mb-4">Code sent to client's email</p>
      <div className="flex gap-2 justify-center mb-4">
        {digits.map((d, i) => (
          <motion.div
            key={i}
            className="w-9 h-11 rounded-lg border border-white/15 bg-white/[0.03] flex items-center justify-center text-sm font-bold text-white"
            initial={{ borderColor: 'rgba(255,255,255,0.15)', backgroundColor: 'rgba(255,255,255,0.03)' }}
            whileInView={{ borderColor: '#3cb44f', backgroundColor: 'rgba(60,180,79,0.08)' }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.5 + i * 0.12 }}
          >
            <motion.span
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 + i * 0.12 }}
            >
              {d}
            </motion.span>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 1.2 }}
        className="flex items-center justify-center gap-1.5 text-[#3cb44f] text-[13px] font-semibold"
      >
        <ShieldCheck size={14} /> Signed
      </motion.div>
    </div>
  );
}

/* ── Illustration 3 — milestone checklist with proof ─────────────────── */
function MilestoneProofIllustration() {
  const milestones = ['Wireframes', 'Visual design', 'Handoff'];
  return (
    <div className="w-full max-w-[240px]">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">Milestones</span>
        <span className="text-[10px] text-white/40">2 / 3</span>
      </div>
      <div className="space-y-1.5 mb-3">
        {milestones.map((m, i) => {
          const done = i < 2;
          return (
            <div key={m} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5">
              <motion.span
                className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 border"
                initial={{ backgroundColor: 'rgba(255,255,255,0)', borderColor: 'rgba(255,255,255,0.2)' }}
                whileInView={done ? { backgroundColor: '#3cb44f', borderColor: '#3cb44f' } : {}}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i === 1 ? 0.9 : 0.2 }}
              >
                {done && (
                  <motion.svg
                    width="8"
                    height="8"
                    viewBox="0 0 10 10"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i === 1 ? 1 : 0.3 }}
                  >
                    <motion.path d="M1 5L4 8L9 2" stroke="black" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </motion.svg>
                )}
              </motion.span>
              <span className={`text-[12.5px] ${done ? 'text-white' : 'text-white/40'}`}>{m}</span>
            </div>
          );
        })}
      </div>
      <motion.div
        className="flex items-center gap-1.5 rounded-lg bg-[#3cb44f]/10 border border-[#3cb44f]/25 px-2.5 py-1.5"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.3, duration: 0.4 }}
      >
        <ShieldCheck size={12} className="text-[#3cb44f] shrink-0" />
        <span className="text-[11px] text-[#3cb44f]">Backed by evidence</span>
      </motion.div>
    </div>
  );
}

const ILLUSTRATIONS = [ContractFormIllustration, OtpSignIllustration, MilestoneProofIllustration];

export default function HowItWorksRevamp() {
  return (
    <section className="relative bg-black py-24 md:py-28 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 30%, black 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 30%, black 20%, transparent 100%)',
        }}
      />

      <div className="relative px-6">
        {/* Docket-row header — the tag row spans the same max-w-6xl the step
            grid below uses, so the rule actually connects to something (the
            grid's right edge) instead of trailing off in empty space inside
            a narrower, separately-centered block. Biggest headline on the
            page — this is the opening content section, right after the
            hero. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-6xl mx-auto mb-14 md:mb-16"
        >
          {/* <div className="flex items-center gap-4 mb-7">
            <span className="font-mono text-[11px] tracking-[0.3em] text-[#3cb44f]/80 uppercase shrink-0">
              Procedure
            </span>
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-[11px] tracking-[0.2em] text-white/30 uppercase shrink-0">03 steps</span>
          </div> */}
          <h2 className="max-w-2xl font-bricolage font-extrabold text-4xl md:text-6xl text-white leading-[1.05] tracking-tight text-left">
            Three steps.
            <br />
            That's the whole{' '}
            <span className="font-instrumentserif italic font-normal text-accent">process</span>.
          </h2>
        </motion.div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-y-14 items-start">
          {STEPS.map((step, i) => {
            const Illustration = ILLUSTRATIONS[i];
            return (
              <Fragment key={step.title}>
                <motion.div
                  className="flex flex-col items-center text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* Fixed-size frame — identical for all three, whatever the
                      illustration inside actually renders. */}
                  <div
                    className={`w-full ${ILLUSTRATION_HEIGHT} rounded-2xl border border-white/10 bg-[#0a0f0a] flex items-center justify-center p-5 mb-6`}
                  >
                    <Illustration />
                  </div>
                  <span className="font-mono text-xs text-[#3cb44f]/70 mb-2">0{i + 1}</span>
                  <h3 className="font-bricolage font-extrabold text-lg md:text-xl text-white mb-2 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-white/55 text-sm leading-relaxed max-w-[260px]">{step.body}</p>
                </motion.div>

                {i < STEPS.length - 1 && (
                  <div className="hidden md:flex items-center justify-center pt-28">
                    <ArrowRight size={18} className="text-white/15" />
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
}
